import React, { useState, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import axios from '../utils/api';
import './CodeEditor.css';

type DeviceRestrictions = Record<string, boolean>;

interface ContentComponent {
  id?: number;
  component_name: string;
  variant_code: string;
  file_path: string;
  css_file_path?: string;
  position_min: number;
  position_max?: number;
  max_usage: number;
  required_components: string[];
  incompatible_components: string[];
  template_restrictions: string[];
  device_restrictions?: DeviceRestrictions;
  performance_impact: string;
  is_active: boolean;
}

interface CodeEditorProps {
  component: ContentComponent | null;
  onSave: (component: ContentComponent, htmlCode: string, cssCode: string) => Promise<void>;
  onCancel: () => void;
}

const CodeEditor: React.FC<CodeEditorProps> = ({ component, onSave, onCancel }) => {
  const [htmlCode, setHtmlCode] = useState<string>('');
  const [cssCode, setCssCode] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'html' | 'css' | 'preview'>('html');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (component) {
      loadComponentCode();
    }
    // Нам важно не менять поведение (загрузка при смене component),
    // поэтому оставляем зависимости как раньше.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [component]);

  const loadComponentCode = async () => {
    if (!component) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Загружаем HTML код
      if (component.file_path && component.id) {
        try {
          const htmlResponse = await axios.get(`/api/admin/components/${component.id}/html`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Если файл существует, используем его содержимое
          // Если файл не существует (content: null), API создаст шаблон по умолчанию
          setHtmlCode(htmlResponse.data.content || generateDefaultHtmlTemplate(component));
        } catch (error) {
          console.log('HTML file not found, generating default template');
          setHtmlCode(generateDefaultHtmlTemplate(component));
        }
      } else {
        setHtmlCode(generateDefaultHtmlTemplate(component));
      }

      // Загружаем CSS код
      if (component.css_file_path && component.id) {
        try {
          const cssResponse = await axios.get(`/api/admin/components/${component.id}/css`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Если файл существует, используем его содержимое
          // Если файл не существует (content: null), API создаст шаблон по умолчанию
          setCssCode(cssResponse.data.content || generateDefaultCssTemplate(component));
        } catch (error) {
          console.log('CSS file not found, generating default template');
          setCssCode(generateDefaultCssTemplate(component));
        }
      } else {
        setCssCode(generateDefaultCssTemplate(component));
      }

    } catch (error: unknown) {
      setError('Ошибка загрузки кода компонента');
      console.error('Error loading component code:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!component) return;

    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Сохраняем HTML код
      if (!component.id) {
        throw new Error('Component ID is required');
      }
      
      await axios.post(`/api/admin/components/${component.id}/html`, {
        content: htmlCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Сохраняем CSS код
      await axios.post(`/api/admin/components/${component.id}/css`, {
        content: cssCode
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await onSave(component, htmlCode, cssCode);
    } catch (error: unknown) {
      setError(getApiErrorMessage(error, 'Ошибка сохранения кода'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="code-editor-overlay">
        <div className="code-editor-modal">
          <div className="loading">Загрузка редактора кода...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="code-editor-overlay">
      <div className="code-editor-modal">
        <div className="editor-header">
          <h2>
            🖥️ Редактор кода: {component?.component_name} ({component?.variant_code})
          </h2>
          <button className="btn-close" onClick={onCancel}>✕</button>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')}>✕</button>
          </div>
        )}

        <div className="editor-tabs">
          <button 
            className={`tab-button ${activeTab === 'html' ? 'active' : ''}`}
            onClick={() => setActiveTab('html')}
          >
            📄 HTML
          </button>
          <button 
            className={`tab-button ${activeTab === 'css' ? 'active' : ''}`}
            onClick={() => setActiveTab('css')}
          >
            🎨 CSS
          </button>
          <button 
            className={`tab-button ${activeTab === 'preview' ? 'active' : ''}`}
            onClick={() => setActiveTab('preview')}
          >
            👁️ Предпросмотр
          </button>
        </div>

        <div className="editor-content">
          {activeTab === 'html' && (
            <div className="editor-panel">
              <div className="editor-toolbar">
                <div>
                  <div>HTML шаблон компонента</div>
                  <small>Используйте {`{{variable}}`} для параметров</small>
                </div>
              </div>
              <Editor
                height="500px"
                language="html"
                theme="vs-dark"
                value={htmlCode}
                onChange={(value) => setHtmlCode(value || '')}
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  wordWrap: 'on',
                  automaticLayout: true,
                  scrollBeyondLastLine: false,
                  folding: true,
                  lineNumbers: 'on',
                  renderWhitespace: 'boundary'
                }}
              />
            </div>
          )}

          {activeTab === 'css' && (
            <div className="editor-panel">
              <div className="editor-toolbar">
                <div>
                  <div>CSS стили компонента</div>
                  <small>Используйте BEM методологию для классов</small>
                </div>
              </div>
              <Editor
                height="500px"
                language="css"
                theme="vs-dark"
                value={cssCode}
                onChange={(value) => setCssCode(value || '')}
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  wordWrap: 'on',
                  automaticLayout: true,
                  scrollBeyondLastLine: false,
                  folding: true,
                  lineNumbers: 'on',
                  renderWhitespace: 'boundary'
                }}
              />
            </div>
          )}

          {activeTab === 'preview' && (
            <div className="preview-panel">
              <div className="preview-toolbar">
                <span>Предпросмотр компонента</span>
                <small>Live preview с текущим HTML и CSS</small>
              </div>
              <iframe
                srcDoc={buildPreviewDocument(htmlCode, cssCode)}
                sandbox="allow-scripts"
                className="preview-frame"
                title="Предпросмотр компонента"
              />
            </div>
          )}
        </div>

        <div className="editor-actions">
          <button className="btn-cancel" onClick={onCancel}>
            Отмена
          </button>
          <button 
            className="btn-save" 
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Сохранение...' : 'Сохранить код'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CodeEditor;

// Простая универсальная функция для генерации базового шаблона
function generateDefaultHtmlTemplate(comp: ContentComponent): string {
  const componentName = comp.component_name;
  const variantCode = comp.variant_code;

  return `<section class="${componentName} ${componentName}-${variantCode}">
  <div class="${componentName}-container">
    <div class="${componentName}-header">
      <h2 class="${componentName}-title">{{title}}</h2>
      <p class="${componentName}-subtitle">{{subtitle}}</p>
    </div>
    
    <div class="${componentName}-content">
      <div class="${componentName}-main">
        <p>{{content}}</p>
      </div>
      
      <div class="${componentName}-actions">
        <button class="btn btn-primary">{{primaryAction}}</button>
        <button class="btn btn-secondary">{{secondaryAction}}</button>
      </div>
    </div>
  </div>
</section>`;
}

// Простая универсальная функция для генерации базовых стилей
function generateDefaultCssTemplate(comp: ContentComponent): string {
  const componentName = comp.component_name;
  const variantCode = comp.variant_code;
  const className = `${componentName}-${variantCode}`;

  return `.${className} {
  /* Базовые стили для ${componentName} (${variantCode}) */
  padding: 2rem 0;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.${componentName}-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
}

.${componentName}-header {
  text-align: center;
  margin-bottom: 2rem;
}

.${componentName}-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.${componentName}-subtitle {
  font-size: 1.125rem;
  color: #7f8c8d;
  line-height: 1.6;
}

.${componentName}-content {
  margin-top: 2rem;
}

.${componentName}-main {
  margin-bottom: 2rem;
}

.${componentName}-main p {
  font-size: 1rem;
  line-height: 1.7;
  color: #555;
  margin-bottom: 1rem;
}

.${componentName}-actions {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.${componentName}-actions .btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-block;
}

.${componentName}-actions .btn-primary {
  background: #3498db;
  color: white;
}

.${componentName}-actions .btn-primary:hover {
  background: #2980b9;
  transform: translateY(-2px);
}

.${componentName}-actions .btn-secondary {
  background: #95a5a6;
  color: white;
}

.${componentName}-actions .btn-secondary:hover {
  background: #7f8c8d;
  transform: translateY(-2px);
}

/* Адаптивность */
@media (max-width: 768px) {
  .${className} {
    padding: 1.5rem 0;
  }
  
  .${componentName}-container {
    padding: 0 1rem;
  }
  
  .${componentName}-title {
    font-size: 2rem;
  }
  
  .${componentName}-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .${componentName}-actions .btn {
    width: 100%;
    max-width: 300px;
  }
}`;
}

function buildPreviewDocument(htmlCode: string, cssCode: string): string {
  return `<!DOCTYPE html>
<html lang="ru">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Предпросмотр компонента</title>
  <style>
    body {
      margin: 0;
      padding: 20px;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      background: #f8f9fa;
    }
    ${cssCode}
  </style>
</head>
<body>
  ${htmlCode}
</body>
</html>`;
}

function getApiErrorMessage(error: unknown, fallbackMessage: string): string {
  if (!error || typeof error !== 'object') {
    return fallbackMessage;
  }

  const errObj = error as Record<string, unknown>;

  const response = errObj.response;
  if (response && typeof response === 'object') {
    const responseObj = response as Record<string, unknown>;
    const data = responseObj.data;
    if (data && typeof data === 'object') {
      const dataObj = data as Record<string, unknown>;
      const apiError = dataObj.error;
      if (typeof apiError === 'string' && apiError.trim()) {
        return apiError;
      }
      const apiMessage = dataObj.message;
      if (typeof apiMessage === 'string' && apiMessage.trim()) {
        return apiMessage;
      }
    }
  }

  const message = errObj.message;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }

  return fallbackMessage;
}
